import { format, startOfDay, addDays } from 'date-fns';
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';

/**
 * Format a date for display
 */
export function formatDate(date: Date, formatStr: string = 'MMM d, yyyy'): string {
  return format(date, formatStr);
}

/**
 * Format time for display
 */
export function formatTime(date: Date, timeZone: string): string {
  return formatInTimeZone(date, timeZone, 'h:mm a');
}

/**
 * Format date label (e.g., "Tue Feb 11")
 */
export function formatDateLabel(date: Date, timeZone: string): string {
  return formatInTimeZone(date, timeZone, 'EEE MMM d');
}

/**
 * Get business days between start and end date
 */
export function getBusinessDays(startDate: Date, endDate: Date): Date[] {
  const days: Date[] = [];
  let currentDate = startOfDay(startDate);
  const end = startOfDay(endDate);

  while (currentDate <= end) {
    const dayOfWeek = currentDate.getDay();
    // Exclude Saturday (6) and Sunday (0)
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      days.push(currentDate);
    }
    currentDate = addDays(currentDate, 1);
  }

  return days;
}

/**
 * Convert time string (HH:mm) to minutes since midnight
 */
export function timeToMinutes(timeStr: string): number {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * Convert minutes since midnight to time string (HH:mm)
 */
export function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

/**
 * Check if a time is within working hours
 */
export function isWithinWorkingHours(
  date: Date,
  workingHoursStart: string,
  workingHoursEnd: string,
  timeZone: string
): boolean {
  const zonedDate = utcToZonedTime(date, timeZone);
  const hours = zonedDate.getHours();
  const minutes = zonedDate.getMinutes();
  const totalMinutes = hours * 60 + minutes;

  const startMinutes = timeToMinutes(workingHoursStart);
  const endMinutes = timeToMinutes(workingHoursEnd);

  return totalMinutes >= startMinutes && totalMinutes < endMinutes;
}

/**
 * Get list of common timezones
 */
export function getCommonTimezones(): Array<{ value: string; label: string }> {
  return [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEDT/AEST)' },
    { value: 'UTC', label: 'UTC' },
  ];
}

/**
 * Create a Date object from date and time in a specific timezone
 *
 * This function takes a date (to determine which calendar day) and a time string,
 * and returns a Date representing that time on that day in the specified timezone.
 */
export function createDateInTimezone(
  date: Date,
  timeStr: string,
  timeZone: string
): Date {
  // Get the date components (year, month, day) in the target timezone
  // This ensures we're working with the correct calendar day regardless of browser timezone
  const year = formatInTimeZone(date, timeZone, 'yyyy');
  const month = formatInTimeZone(date, timeZone, 'MM');
  const day = formatInTimeZone(date, timeZone, 'dd');

  // Create a date-time string in the format zonedTimeToUtc expects
  // This represents "the local time in the target timezone"
  const dateTimeStr = `${year}-${month}-${day} ${timeStr}:00`;

  // zonedTimeToUtc interprets the string as a local time in the target timezone
  // and returns the corresponding UTC Date
  return zonedTimeToUtc(dateTimeStr, timeZone);
}
